import { useCallback, useRef } from 'react';

import { usePbkAuth } from '../contexts/PbkAuthContext';
import AuthService from '../services/AuthService';
import useUnauthenticate from './useUnauthenticate';

let refreshPromise: Promise<boolean> | null = null;
// Web Locks are atomic across tabs and released on unload, unlike a localStorage flag
const REFRESH_LOCK_NAME = 'pbk_refresh_token';

const useRefreshToken = (externalLogoutFn?: () => void) => {
  const pbkAuth = usePbkAuth();
  const logoutRef = useRef(pbkAuth?.logout);
  const oldLogout = useUnauthenticate();
  logoutRef.current = pbkAuth?.logout;

  return useCallback(
    async (secondTry?: boolean) => {
      // If another call is already running in this tab
      if (refreshPromise) {
        return refreshPromise;
      }

      const doRefresh = async () => {
        let tokenRefreshed: boolean;
        try {
          const response = await AuthService.refreshToken(secondTry);
          tokenRefreshed = !!response?.meta?.success;
        } catch {
          tokenRefreshed = false;
        }

        if (!tokenRefreshed) {
          if (externalLogoutFn) externalLogoutFn();
          else if (logoutRef.current) logoutRef.current();
          else oldLogout();
        }

        return tokenRefreshed;
      };

      refreshPromise = (async () => {
        if (!navigator.locks) {
          return doRefresh();
        }

        // `null` lock means another tab holds it; refreshing again would reuse a rotated token
        const result = await navigator.locks.request(REFRESH_LOCK_NAME, { ifAvailable: true }, (lock) => (lock ? doRefresh() : null));
        if (result !== null) {
          return result;
        }

        // Wait for the other tab to finish; its refreshed cookie is shared with this tab
        await navigator.locks.request(REFRESH_LOCK_NAME, () => undefined);
        return true;
      })();

      try {
        return await refreshPromise;
      } finally {
        refreshPromise = null;
      }
    },
    [externalLogoutFn, oldLogout],
  );
};

export default useRefreshToken;
